import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";
import {
  ArrowLeft,
  Lock,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Video,
  Facebook,
  Instagram,
  Heart,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const [social, setSocial] = useState<{
    facebook_url: string | null;
    instagram_url: string | null;
  }>({ facebook_url: null, instagram_url: null });

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("public-settings");
        if (data && Array.isArray(data) && data.length > 0) {
          setSocial({
            facebook_url: data[0].facebook_url ?? null,
            instagram_url: data[0].instagram_url ?? null,
          });
        }
      } catch (e) {
        // silent fail
      }
    })();
  }, []);

  const scrollToBooking = () => {
    window.location.href = "/#accompagnements";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src={brandLogo} alt={BRAND.name.full} className="w-9 sm:w-10 md:w-12 h-auto object-contain" />
            <div className="text-left">
              <div className="font-cinzel font-bold leading-tight">
                <span className="text-secondary text-sm sm:text-base md:text-lg">{BRAND.name.line1}</span>
                <span className="text-primary text-sm sm:text-base md:text-lg"> {BRAND.name.line2}</span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-montserrat uppercase tracking-wide">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/">
              <Button variant="ghost" size="sm" className="font-montserrat">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Retour</span>
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-primary font-montserrat"
              >
                <Lock className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Espace Pro</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="py-6 sm:py-8 md:py-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <img
              src={brandLogo}
              alt={BRAND.name.full}
              className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 mx-auto object-contain"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-cinzel font-bold text-primary leading-tight tracking-wide">
              ME CONTACTER
            </h1>
            <div className="w-20 h-px bg-secondary mx-auto" />
            <p className="text-base sm:text-lg md:text-xl text-foreground/80 font-montserrat">
              <span className="whitespace-nowrap">Une question, un doute ou besoin d'échanger avant de prendre rendez-vous&nbsp;?</span>
              <br />
              Je suis à votre écoute.
            </p>
          </div>
        </div>
      </section>

      {/* COMMENT ME CONTACTER */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide text-center mb-2">
              Comment me contacter
            </h2>
            <div className="w-16 h-px bg-secondary mx-auto mb-8 sm:mb-10" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-primary/20 hover:shadow-elegant transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8 text-center space-y-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center mx-auto">
                    <Mail className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-cinzel font-semibold text-primary">Email</h3>
                  <a
                    href="mailto:contact@regard-de-nature.fr"
                    className="block text-base font-montserrat text-foreground hover:text-primary transition-colors break-all"
                  >
                    contact@regard-de-nature.fr
                  </a>
                  <p className="text-sm text-muted-foreground font-montserrat italic">
                    Je vous réponds dans les meilleurs délais.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:shadow-elegant transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8 text-center space-y-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center mx-auto">
                    <Phone className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-cinzel font-semibold text-primary">Téléphone</h3>
                  <a
                    href="sms:+33637031866"
                    className="block text-base font-montserrat text-foreground hover:text-primary transition-colors"
                  >
                    06 37 03 18 66
                  </a>
                  <p className="text-sm text-muted-foreground font-montserrat italic">
                    Disponible par SMS si vous souhaitez échanger avant de prendre rendez-vous.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PRENDRE RENDEZ-VOUS */}
      <section className="py-10 sm:py-14 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide mb-2">
              Prendre rendez-vous
            </h2>
            <div className="w-16 h-px bg-secondary mx-auto mb-6" />
            <p className="text-base sm:text-lg text-foreground/80 font-montserrat mb-8">
              Si vous souhaitez démarrer un accompagnement, vous pouvez réserver directement une consultation en ligne.
            </p>
            <Button
              size="lg"
              className="bg-gradient-primary text-primary-foreground hover:opacity-90 font-montserrat h-12 px-8"
              onClick={scrollToBooking}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Réserver un rendez-vous
            </Button>
          </div>
        </div>
      </section>

      {/* LIEUX DE CONSULTATION */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide text-center mb-2">
              Lieux de consultation
            </h2>
            <div className="w-16 h-px bg-secondary mx-auto mb-4" />
            <p className="text-base sm:text-lg text-foreground/80 font-montserrat text-center mb-8 sm:mb-10 max-w-2xl mx-auto">
              Les consultations peuvent se faire en présentiel ou à distance, selon vos besoins et l'accompagnement choisi.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-primary/20 hover:shadow-elegant transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8 space-y-3 flex flex-col h-full">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-secondary flex-shrink-0" />
                    <h3 className="text-lg font-cinzel font-semibold text-primary">
                      Montesquieu-Lauragais
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed">
                    Les consultations se déroulent dans un espace dédié à mon activité, aménagé au sein de mon lieu de vie, offrant un cadre calme, confidentiel et propice à l'échange.
                  </p>
                  <p className="text-sm text-muted-foreground font-montserrat italic">
                    Stationnement possible directement sur place.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="font-montserrat border-primary text-primary hover:bg-primary/10"
                    >
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=31+rue+de+l%27ancien+petit+foss%C3%A9%2C+31450+Montesquieu-Lauragais"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Google Maps
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="font-montserrat border-primary text-primary hover:bg-primary/10"
                    >
                      <a
                        href="https://maps.apple.com/?q=31+rue+de+l%27ancien+petit+foss%C3%A9%2C+31450+Montesquieu-Lauragais"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Plans
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:shadow-elegant transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8 space-y-3 flex flex-col h-full">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-secondary flex-shrink-0" />
                    <h3 className="text-lg font-cinzel font-semibold text-primary">
                      Villefranche-de-Lauragais – La Maison Céleste
                    </h3>
                  </div>
                  <p className="text-xs font-montserrat text-secondary uppercase tracking-wide">
                    Jeudi matin
                  </p>
                  <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed">
                    Lieu de ressources et de bien-être, la Maison Céleste offre un cadre apaisant et chaleureux. Les consultations ont lieu dans une salle dédiée, propice à l'écoute et à la détente.
                  </p>
                  <p className="text-sm text-muted-foreground font-montserrat italic">
                    Des places de stationnement sont disponibles à proximité.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="font-montserrat border-primary text-primary hover:bg-primary/10"
                    >
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=La+Maison+C%C3%A9leste+Villefranche-de-Lauragais"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Google Maps
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="font-montserrat border-primary text-primary hover:bg-primary/10"
                    >
                      <a
                        href="https://maps.apple.com/?q=La+Maison+C%C3%A9leste+Villefranche-de-Lauragais"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Plans
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-primary/20 mt-6 bg-muted/30 max-w-md mx-auto">
              <CardContent className="p-6 sm:p-8 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center flex-shrink-0">
                  <Video className="w-5 h-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-cinzel font-semibold text-primary">
                  Consultations en visio
                </h3>
                <p className="text-sm sm:text-base text-foreground/80 font-montserrat">
                  Possibles selon l'accompagnement choisi.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ME SUIVRE */}
      {(social.facebook_url || social.instagram_url) && (
        <section className="py-10 sm:py-14 bg-card">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide mb-2">
                Me suivre
              </h2>
              <div className="w-16 h-px bg-secondary mx-auto mb-6" />
              <div className="flex items-center justify-center gap-6">
                {social.instagram_url && (
                  <a
                    href={social.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground hover:opacity-90 transition-opacity"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {social.facebook_url && (
                  <a
                    href={social.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground hover:opacity-90 transition-opacity"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BESOIN D'ÊTRE RASSURÉ(E) */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <Card className="border-secondary/30 bg-secondary/5">
              <CardContent className="p-6 sm:p-8 md:p-10 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-secondary/15 flex items-center justify-center mx-auto">
                  <Heart className="w-6 h-6 text-secondary" />
                </div>
                <h2 className="text-lg sm:text-2xl font-cinzel font-bold text-primary uppercase tracking-wide break-words">
                  Besoin d'être rassuré(e)
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> </span>
                  avant de commencer&nbsp;?
                </h2>
                <div className="w-12 h-px bg-secondary mx-auto" />
                <p className="text-base sm:text-lg text-foreground/80 font-montserrat leading-relaxed">
                  Il est tout à fait normal d'hésiter avant de se lancer.
                  <br />
                  Vous pouvez me contacter simplement par email pour échanger.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="font-montserrat h-12 px-6 border-primary text-primary hover:bg-primary/10"
                >
                  <a href="mailto:contact@regard-de-nature.fr">
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer un email
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer simple */}
      <footer className="bg-card border-t border-border py-6 sm:py-8">
        <div className="container mx-auto px-4 text-center text-xs sm:text-sm text-muted-foreground font-montserrat">
          <p>{BRAND.legal.copyright(new Date().getFullYear())}</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
